import { useContext } from 'react';
import { ModalContext, ModalType } from '../../context/ModalContext';
import { useGetUserFromAccessToken } from '../../common/auth';
import { SelfSortVariant } from './selfSort.constants';

type Props = {
  /** Resolved by useSelfSort upstream; undefined while role/org are still loading. */
  variant: SelfSortVariant | undefined;
  collapsed?: boolean;
};

/**
 * Persistent sidebar nudge. Visibility is gated per collection scope, not by one
 * global flag:
 *   - member (role, a per-USER answer) hides once `selfSortCompletedAt` is set;
 *   - owner (firmographics, a per-ORG answer) is emitted by resolveSelfSortVariant
 *     only while THIS org lacks firmographics, so it must show even for a returning
 *     owner who already completed self-sort in another org (#2659). It hides when the
 *     org's companySize is saved and the variant flips to member.
 * `roleKnown` tells the modal to skip the already-answered role question.
 */
export default function SelfSortBanner({ variant, collapsed }: Props) {
  const { openModal } = useContext(ModalContext);
  const { data: user } = useGetUserFromAccessToken();

  const roleKnown = user?.user.selfSortCompletedAt != null;
  if (!variant || (variant === 'member' && roleKnown)) return null;

  const open = () => openModal(ModalType.SelfSort, { variant, roleKnown });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') open();
      }}
      className={`mb-2 rounded-lg cursor-pointer border border-solid border-purple-300 bg-purple-100 text-brand-grey-dark ${
        collapsed ? 'flex items-center justify-center py-2' : 'flex flex-col gap-1 py-2 pl-[18px] pr-[22px]'
      }`}
    >
      {collapsed ? (
        <span className="text-base leading-none">🚀</span>
      ) : (
        <>
          <span className="text-[10px] font-medium leading-3">🚀 Answer quick questions to</span>
          <span className="text-sm font-medium leading-5 whitespace-nowrap">Help us build a better product</span>
        </>
      )}
    </div>
  );
}
